import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDownTrayIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'
import DateInput from '@/components/DateInput'
import VendorSpendTrendChart from '@/components/VendorSpendTrendChart'
import { useDebounce } from '@/hooks/useDebounce'
import { formatAmount } from '@/pages/vendorDetailsTableConfig'
import {
  getPurchasingVendorProfile,
  updatePurchasingVendorProfile,
  type PurchasingVendorProfile,
} from '@/services/purchasing'
import {
  getVendorSpendTransactions,
  type SpendAnalyticsBasis,
  type SpendAnalyticsGranularity,
  type SpendAnalyticsGroupBy,
  type SpendTransaction,
  type VendorDetailItem,
  type VendorSpendAnalyticsResponse,
} from '@/services/vendorDetails'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { downloadCsvFile } from '@/utils/warehouseTableHelpers'
import {
  buildVendorSpendExport,
  computeVendorSpendAnalytics,
} from '@/utils/vendorSpendAnalytics'
import {
  getVendorSpendFromCache,
  setVendorSpendCache,
} from '@/utils/vendorSpendTransactionCache'

type SidebarTab = 'spend' | 'purchasing'

const SPEND_DEFINITION =
  'Net = bills and purchases minus vendor credits. Gross = bills and purchases before credits. Bill payments are excluded to avoid double-counting.'

const LOCATION_HELPER =
  'Location is the QBO department on each expense line (or transaction header). Lines without a department are grouped as Unassigned.'

interface TransactionCache {
  vendorId: string
  startDate: string
  endDate: string
  transactions: SpendTransaction[]
  displayName: string
}

function formatIsoDateLocal(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function resolveDefaultRange(start: string, end: string): { start: string; end: string } {
  if (start && end) return { start, end }
  const endDate = new Date()
  const startDate = new Date(endDate)
  startDate.setFullYear(startDate.getFullYear() - 1)
  startDate.setDate(startDate.getDate() + 1)
  return { start: formatIsoDateLocal(startDate), end: formatIsoDateLocal(endDate) }
}

function cacheFromLru(vendorId: string): TransactionCache | null {
  const entry = getVendorSpendFromCache(vendorId)
  if (!entry) return null
  return {
    vendorId,
    startDate: entry.startDate,
    endDate: entry.endDate,
    transactions: entry.transactions,
    displayName: entry.displayName,
  }
}

function needsNetworkFetch(
  cache: TransactionCache | null,
  vendorId: string,
  startDate: string,
  endDate: string,
  forceRefresh: boolean
): boolean {
  if (forceRefresh) return true
  if (!cache || cache.vendorId !== vendorId) return true
  if (startDate < cache.startDate || endDate > cache.endDate) return true
  return false
}

function SegmentedToggle<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-gray-500">{label}</p>
      <div
        className="inline-flex rounded-lg bg-gray-200 p-0.5"
        role="group"
        aria-label={label}
      >
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
              value === opt.value
                ? 'bg-white text-brand-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

interface VendorSpendSidebarProps {
  vendor: VendorDetailItem | null
  open: boolean
  onClose: () => void
  defaultStartDate: string
  defaultEndDate: string
}

export default function VendorSpendSidebar({
  vendor,
  open,
  onClose,
  defaultStartDate,
  defaultEndDate,
}: VendorSpendSidebarProps) {
  const resolvedDefaults = useMemo(
    () => resolveDefaultRange(defaultStartDate, defaultEndDate),
    [defaultStartDate, defaultEndDate]
  )

  const [startDate, setStartDate] = useState(resolvedDefaults.start)
  const [endDate, setEndDate] = useState(resolvedDefaults.end)
  const [granularity, setGranularity] = useState<SpendAnalyticsGranularity>('month')
  const [basis, setBasis] = useState<SpendAnalyticsBasis>('net')
  const [groupBy, setGroupBy] = useState<SpendAnalyticsGroupBy>('category')
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('spend')
  const [cache, setCache] = useState<TransactionCache | null>(null)
  const [fetching, setFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [purchasingProfile, setPurchasingProfile] = useState<PurchasingVendorProfile | null>(null)
  const [purchasingLoading, setPurchasingLoading] = useState(false)
  const [purchasingError, setPurchasingError] = useState<string | null>(null)
  const [purchasingNotes, setPurchasingNotes] = useState('')
  const [purchasingSaving, setPurchasingSaving] = useState(false)
  const [showFlipNewModal, setShowFlipNewModal] = useState(false)
  const currentVendorIdRef = useRef<string | null>(vendor?.id ?? null)
  const forceRefreshRef = useRef(false)

  const debouncedStart = useDebounce(startDate, 300)
  const debouncedEnd = useDebounce(endDate, 300)

  useEffect(() => {
    currentVendorIdRef.current = vendor?.id ?? null
  }, [vendor?.id])

  useEffect(() => {
    if (!open || !vendor) return
    setStartDate(resolvedDefaults.start)
    setEndDate(resolvedDefaults.end)
    setGranularity('month')
    setBasis('net')
    setGroupBy('category')
    setSidebarTab('spend')
    setCache(cacheFromLru(vendor.id))
    setError(null)
    setFetching(false)
    setPurchasingProfile(null)
    setPurchasingError(null)
    setPurchasingNotes('')
    setShowFlipNewModal(false)
    forceRefreshRef.current = false
  }, [open, vendor, resolvedDefaults.start, resolvedDefaults.end])

  useEffect(() => {
    if (!open) return
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, onClose])

  useEffect(() => {
    if (!open || !vendor || sidebarTab !== 'purchasing') return

    const requestVendorId = vendor.id
    setPurchasingLoading(true)
    setPurchasingError(null)

    void (async () => {
      try {
        const profile = await getPurchasingVendorProfile(requestVendorId)
        if (requestVendorId !== currentVendorIdRef.current) return
        setPurchasingProfile(profile)
        setPurchasingNotes(profile.notes ?? '')
      } catch (err) {
        if (requestVendorId !== currentVendorIdRef.current) return
        setPurchasingError(formatApiError(err, 'Failed to load purchasing profile'))
      } finally {
        if (requestVendorId === currentVendorIdRef.current) {
          setPurchasingLoading(false)
        }
      }
    })()
  }, [open, vendor, sidebarTab])

  const loadTransactions = useCallback(async () => {
    if (!vendor || !debouncedStart || !debouncedEnd) return

    if (debouncedStart > debouncedEnd) {
      setError('Start date must be on or before end date.')
      return
    }

    const forceRefresh = forceRefreshRef.current
    forceRefreshRef.current = false

    if (!needsNetworkFetch(cache, vendor.id, debouncedStart, debouncedEnd, forceRefresh)) {
      return
    }

    const requestVendorId = vendor.id
    setFetching(true)
    setError(null)

    try {
      const result = await getVendorSpendTransactions(requestVendorId, {
        startDate: debouncedStart,
        endDate: debouncedEnd,
      })
      if (requestVendorId !== currentVendorIdRef.current) return
      const nextCache: TransactionCache = {
        vendorId: result.vendor_id,
        startDate: result.start_date,
        endDate: result.end_date,
        transactions: result.transactions,
        displayName: result.display_name,
      }
      setVendorSpendCache(result.vendor_id, {
        startDate: nextCache.startDate,
        endDate: nextCache.endDate,
        transactions: nextCache.transactions,
        displayName: nextCache.displayName,
      })
      setCache(nextCache)
    } catch (err) {
      if (requestVendorId !== currentVendorIdRef.current) return
      setError(formatApiError(err, 'Failed to load spend analytics'))
    } finally {
      if (requestVendorId === currentVendorIdRef.current) {
        setFetching(false)
      }
    }
  }, [vendor, debouncedStart, debouncedEnd, cache])

  useEffect(() => {
    if (!open || !vendor || sidebarTab !== 'spend') return
    void loadTransactions()
  }, [open, vendor, sidebarTab, debouncedStart, debouncedEnd, loadTransactions])

  const handleRefresh = useCallback(() => {
    forceRefreshRef.current = true
    void loadTransactions()
  }, [loadTransactions])

  const analytics = useMemo((): VendorSpendAnalyticsResponse | null => {
    if (!vendor || !cache || cache.vendorId !== vendor.id) return null
    if (!debouncedStart || !debouncedEnd || debouncedStart > debouncedEnd) return null
    if (debouncedStart < cache.startDate || debouncedEnd > cache.endDate) return null

    return computeVendorSpendAnalytics(
      cache.transactions,
      cache.vendorId,
      cache.displayName,
      debouncedStart,
      debouncedEnd,
      granularity,
      basis,
      groupBy
    )
  }, [vendor, cache, debouncedStart, debouncedEnd, granularity, basis, groupBy])

  const exportDisabled =
    !vendor ||
    Boolean(error) ||
    fetching ||
    analytics == null ||
    cache == null ||
    cache.vendorId !== vendor.id ||
    analytics.vendor_id !== vendor.id ||
    !debouncedStart ||
    !debouncedEnd ||
    debouncedStart > debouncedEnd

  const handleExport = useCallback(() => {
    if (exportDisabled || !analytics || !cache) return
    const { filename, csvBody } = buildVendorSpendExport(analytics, cache.transactions)
    downloadCsvFile(filename, csvBody)
    toast.success('CSV downloaded')
  }, [analytics, cache, exportDisabled])

  const savePurchasingProfile = useCallback(
    async (payload: Parameters<typeof updatePurchasingVendorProfile>[1]) => {
      if (!vendor) return
      setPurchasingSaving(true)
      try {
        const updated = await updatePurchasingVendorProfile(vendor.id, payload)
        setPurchasingProfile(updated)
        setPurchasingNotes(updated.notes ?? '')
        toast.success('Purchasing profile updated')
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to update purchasing profile'))
      } finally {
        setPurchasingSaving(false)
      }
    },
    [vendor],
  )

  const handleFlipToNew = useCallback(async () => {
    if (!vendor) return
    setShowFlipNewModal(false)
    setPurchasingSaving(true)
    try {
      const updated = await updatePurchasingVendorProfile(vendor.id, {
        bill_extraction_mode: 'new',
      })
      setPurchasingProfile(updated)
      toast.success('Bill extraction mode set to new')
    } catch (err) {
      toast.error(formatApiError(err, 'Could not flip to new extraction mode'))
    } finally {
      setPurchasingSaving(false)
    }
  }, [vendor])

  if (!open || !vendor) return null

  const dataMatchesVendor =
    analytics != null && cache?.vendorId === vendor.id && analytics.vendor_id === vendor.id
  const breakdown = dataMatchesVendor ? (analytics?.segment_breakdown ?? []) : []
  const totals = dataMatchesVendor ? analytics?.totals : undefined
  const periods = dataMatchesVendor ? (analytics?.periods ?? []) : []
  const showLoading = !error && (fetching || analytics == null)
  const segmentColumnLabel = groupBy === 'location' ? 'Location' : 'Category'
  const breakdownHeading = groupBy === 'location' ? 'By location' : 'By category'
  const emptyBreakdownMessage =
    groupBy === 'location'
      ? 'No location-tagged spend in this range.'
      : 'No categorized spend in this range.'

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/10"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed right-0 top-0 z-50 flex h-full w-[min(560px,92vw)] flex-col border-l border-gray-200 bg-white shadow-xl"
        aria-label={`Spend analytics for ${vendor.display_name}`}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900">
              {vendor.display_name}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-gray-500">
              {sidebarTab === 'spend' ? SPEND_DEFINITION : 'Purchasing profile and bill extraction settings'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {sidebarTab === 'spend' ? (
              <button
                type="button"
                onClick={handleExport}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-40"
                aria-label="Export CSV"
                disabled={exportDisabled}
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            ) : null}
            {sidebarTab === 'spend' ? (
              <button
                type="button"
                onClick={handleRefresh}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Refresh analytics"
                disabled={fetching}
              >
                <ArrowPathIcon className={`h-4 w-4 ${fetching ? 'animate-spin' : ''}`} />
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="shrink-0 border-b border-gray-200 px-4 py-2">
          <SegmentedToggle
            label="View"
            options={[
              { value: 'spend', label: 'Spend' },
              { value: 'purchasing', label: 'Purchasing' },
            ]}
            value={sidebarTab}
            onChange={setSidebarTab}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {sidebarTab === 'purchasing' ? (
            <div className="space-y-4">
              {purchasingLoading ? (
                <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Loading purchasing profile…
                </div>
              ) : purchasingError ? (
                <p className="py-4 text-sm text-red-600">{purchasingError}</p>
              ) : purchasingProfile ? (
                <>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      checked={purchasingProfile.enabled}
                      disabled={purchasingSaving}
                      onChange={(e) =>
                        void savePurchasingProfile({ enabled: e.target.checked })
                      }
                    />
                    <span>COGS purchasing enabled</span>
                  </label>

                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-500">Parser key</p>
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 ring-1 ring-inset ring-gray-200">
                      {purchasingProfile.parser_key || '—'}
                    </span>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">
                      Hints / notes
                    </label>
                    <textarea
                      className="input min-h-[5rem] w-full resize-y text-sm"
                      value={purchasingNotes}
                      disabled={purchasingSaving}
                      onChange={(e) => setPurchasingNotes(e.target.value)}
                      onBlur={() => {
                        if ((purchasingProfile.notes ?? '') === purchasingNotes) return
                        void savePurchasingProfile({ notes: purchasingNotes || null })
                      }}
                      placeholder="Vendor-specific extraction hints…"
                    />
                  </div>

                  <div>
                    <SegmentedToggle
                      label="Bill extraction mode"
                      options={[
                        { value: 'legacy', label: 'Legacy' },
                        { value: 'shadow', label: 'Shadow' },
                        { value: 'new', label: 'New' },
                      ]}
                      value={
                        (['legacy', 'shadow', 'new'] as const).includes(
                          purchasingProfile.bill_extraction_mode as 'legacy' | 'shadow' | 'new',
                        )
                          ? (purchasingProfile.bill_extraction_mode as 'legacy' | 'shadow' | 'new')
                          : 'legacy'
                      }
                      onChange={(mode) => {
                        if (mode === 'new' && purchasingProfile.bill_extraction_mode !== 'new') {
                          setShowFlipNewModal(true)
                          return
                        }
                        if (mode !== purchasingProfile.bill_extraction_mode) {
                          void savePurchasingProfile({ bill_extraction_mode: mode })
                        }
                      }}
                    />
                  </div>

                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <p className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Shadow agreement
                    </p>
                    <table className="min-w-full text-xs">
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Agree</th>
                          <td className="px-3 py-2 tabular-nums text-gray-900">
                            {purchasingProfile.bill_extraction_shadow.agree_count}
                          </td>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Total</th>
                          <td className="px-3 py-2 tabular-nums text-gray-900">
                            {purchasingProfile.bill_extraction_shadow.total}
                          </td>
                        </tr>
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Rate</th>
                          <td className="px-3 py-2 tabular-nums text-gray-900">
                            {purchasingProfile.bill_extraction_shadow.agreement_rate != null
                              ? `${(purchasingProfile.bill_extraction_shadow.agreement_rate * 100).toFixed(1)}%`
                              : '—'}
                          </td>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Updated</th>
                          <td className="px-3 py-2 text-gray-900">
                            {purchasingProfile.bill_extraction_shadow.updated_at?.slice(0, 10) ?? '—'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {Object.keys(purchasingProfile.bill_extraction_shadow.category_counts).length >
                    0 ? (
                      <table className="min-w-full border-t border-gray-200 text-xs">
                        <thead>
                          <tr className="bg-gray-50 text-left text-gray-500">
                            <th className="px-3 py-2 font-medium">Category</th>
                            <th className="px-3 py-2 font-medium text-right">Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(
                            purchasingProfile.bill_extraction_shadow.category_counts,
                          ).map(([category, count]) => (
                            <tr key={category} className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-700">{category}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                                {count}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">Start</label>
                <DateInput
                  value={startDate}
                  onChange={setStartDate}
                  className="input input-sm w-[7.5rem]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">End</label>
                <DateInput
                  value={endDate}
                  onChange={setEndDate}
                  className="input input-sm w-[7.5rem]"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <SegmentedToggle
                label="Granularity"
                options={[
                  { value: 'month', label: 'Month' },
                  { value: 'quarter', label: 'Quarter' },
                  { value: 'year', label: 'Year' },
                ]}
                value={granularity}
                onChange={setGranularity}
              />
              <SegmentedToggle
                label="Basis"
                options={[
                  { value: 'net', label: 'Net' },
                  { value: 'gross', label: 'Gross' },
                ]}
                value={basis}
                onChange={setBasis}
              />
              <SegmentedToggle
                label="Group by"
                options={[
                  { value: 'category', label: 'Category' },
                  { value: 'location', label: 'Location' },
                ]}
                value={groupBy}
                onChange={setGroupBy}
              />
            </div>

            {groupBy === 'location' ? (
              <p className="text-xs leading-relaxed text-gray-500">{LOCATION_HELPER}</p>
            ) : null}

            {error ? (
              <p className="py-4 text-sm text-red-600">{error}</p>
            ) : showLoading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Loading spend analytics…
              </div>
            ) : dataMatchesVendor && analytics ? (
              <div className="space-y-4">
                <VendorSpendTrendChart
                  periods={periods}
                  basis={basis}
                  granularity={granularity}
                  segmentLabel={groupBy}
                />

                {totals ? (
                  <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs sm:grid-cols-5">
                    <div>
                      <p className="text-gray-500">Net</p>
                      <p className="tabular-nums font-medium text-gray-900">
                        {formatAmount(totals.net)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Gross</p>
                      <p className="tabular-nums font-medium text-gray-900">
                        {formatAmount(totals.gross)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Expense txns</p>
                      <p className="tabular-nums font-medium text-gray-900">
                        {totals.expense_txn_count}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Bills</p>
                      <p className="tabular-nums font-medium text-gray-900">
                        {totals.bill_count}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Credits</p>
                      <p className="tabular-nums font-medium text-gray-900">
                        {totals.vendor_credit_count}
                      </p>
                    </div>
                  </div>
                ) : null}

                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {breakdownHeading}
                  </h3>
                  {breakdown.length === 0 ? (
                    <p className="text-sm text-gray-500">{emptyBreakdownMessage}</p>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-600">
                            <th className="px-3 py-2 font-medium">{segmentColumnLabel}</th>
                            <th className="px-3 py-2 text-right font-medium">
                              {basis === 'net' ? 'Net' : 'Gross'}
                            </th>
                            <th className="px-3 py-2 text-right font-medium">% of total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {breakdown.map((row) => {
                            const amount = basis === 'net' ? row.net : row.gross
                            return (
                              <tr
                                key={row.segment}
                                className="border-b border-gray-100 last:border-0"
                              >
                                <td
                                  className="max-w-[12rem] truncate px-3 py-2 text-gray-900"
                                  title={row.segment}
                                >
                                  {row.segment}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                                  {formatAmount(amount)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                                  {row.pct.toFixed(1)}%
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
          )}
        </div>
      </aside>

      {showFlipNewModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !purchasingSaving && setShowFlipNewModal(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900">Flip to new extraction mode?</h3>
            </div>
            <div className="space-y-2 p-4 text-sm text-gray-600">
              <p>
                This vendor will use the new bill reader for ingestion. Flip is gated on shadow
                agreement stats unless you force it from the API.
              </p>
              <p className="text-gray-500">
                If flip rules are not met, the server will return an error with the unmet
                conditions.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 p-4">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={purchasingSaving}
                onClick={() => setShowFlipNewModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={purchasingSaving}
                onClick={() => void handleFlipToNew()}
              >
                {purchasingSaving ? 'Updating…' : 'Flip to new'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
